import { Link } from "react-router-dom";
import { Briefcase, Mail, MapPin, Phone } from "lucide-react";
import { useCompanySettings } from "@/context/SettingsContext";

const FooterCol = ({ title, children }) => (
  <div>
    <p className="text-sm font-semibold text-foreground mb-3">{title}</p>
    <div className="space-y-2">{children}</div>
  </div>
);

const FooterLink = ({ to, children, testId }) => (
  <Link to={to} data-testid={testId} className="block text-sm text-muted-foreground hover:text-foreground transition-colors">
    {children}
  </Link>
);

export const PublicFooter = () => {
  const { settings } = useCompanySettings();
  return (
    <footer className="border-t border-border bg-white" data-testid="public-footer">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
          <div>
            <div className="flex items-center gap-2">
              <span className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center">
                <Briefcase className="h-4 w-4 text-white" />
              </span>
              <span className="font-heading font-semibold">
                Udhyog<span className="text-primary">Tech</span>
              </span>
            </div>
            <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{settings.footer_tagline}</p>
            <span className="inline-flex items-center rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-semibold px-3 py-1 mt-3" data-testid="footer-free-badge">
              100% FREE for Job Seekers
            </span>
          </div>

          <FooterCol title="Quick Links">
            <FooterLink to="/jobs" testId="footer-jobs-link">Find Jobs</FooterLink>
            <FooterLink to="/resume-builder" testId="footer-resume-link">Free Resume Builder</FooterLink>
            <FooterLink to="/login" testId="footer-track-link">Track My Application</FooterLink>
            <FooterLink to="/about" testId="footer-about-link">About Us</FooterLink>
            <FooterLink to="/contact" testId="footer-contact-link">Contact Us</FooterLink>
          </FooterCol>

          <FooterCol title="Legal">
            <FooterLink to="/terms" testId="footer-terms-link">Terms & Conditions</FooterLink>
            <FooterLink to="/privacy" testId="footer-privacy-link">Privacy Policy</FooterLink>
            <FooterLink to="/cookie-policy" testId="footer-cookie-link">Cookie Policy</FooterLink>
            <FooterLink to="/disclaimer" testId="footer-disclaimer-link">Disclaimer</FooterLink>
          </FooterCol>

          <FooterCol title="Contact">
            {settings.office_address && (
              <p className="flex items-start gap-2 text-sm text-muted-foreground" data-testid="footer-address">
                <MapPin className="h-4 w-4 shrink-0 mt-0.5" /> {settings.office_address}
              </p>
            )}
            {settings.contact_number && (
              <p className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="footer-phone">
                <Phone className="h-4 w-4 shrink-0" /> {settings.contact_number}
              </p>
            )}
            {settings.support_email && (
              <p className="flex items-center gap-2 text-sm text-muted-foreground break-all" data-testid="footer-email">
                <Mail className="h-4 w-4 shrink-0" /> {settings.support_email}
              </p>
            )}
          </FooterCol>
        </div>

        <div className="border-t border-border mt-10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-2">
          <p className="text-xs text-muted-foreground">© {new Date().getFullYear()} {settings.company_name}. All rights reserved.</p>
          <p className="text-xs text-muted-foreground">No registration fee · No placement charges · No hidden charges</p>
        </div>
      </div>
    </footer>
  );
};
